import { useMutation } from '@tanstack/react-query';
import { rpcClient } from '@/api/rpc/client';
import type { RemoveDocumentResponse } from '@erp/rpc';
import { useToast } from '@/context/ToastContext';
import { DocumentAssociationType } from '@erp/shared-types';

interface UploadEntityDocumentInput {
  entityType: string;
  entityId: string;
  file: File;
  associationType?: DocumentAssociationType;
}

interface UploadEntityDocumentResult {
  documentId: string;
  name: string;
  mimeType: string | null;
  fileSizeBytes: number | null;
  fileId: string | null | undefined;
}

/**
 * Generic hook for uploading a document and associating it with any entity.
 * Handles the full presigned URL flow:
 * 1. Get presigned URL from backend
 * 2. Upload file directly to storage
 * 3. Confirm upload with backend
 *
 * Caller is responsible for cache invalidation / refetching on success.
 */
export function useUploadEntityDocument() {
  const { showToast } = useToast();

  return useMutation<
    UploadEntityDocumentResult,
    Error,
    UploadEntityDocumentInput
  >({
    mutationFn: async ({ entityType, entityId, file, associationType }) => {
      // Step 1: Get presigned upload URL
      const { documentId, uploadUrl } =
        await rpcClient.core.documents.getUploadUrl({
          entityType,
          entityId,
          fileName: file.name,
          contentType: file.type || 'application/octet-stream',
        });

      // Step 2: Upload file directly to storage
      const headers: Record<string, string> = {
        'Content-Type': file.type || 'application/octet-stream',
      };
      if (uploadUrl.includes('.blob.core.windows.net')) {
        headers['x-ms-blob-type'] = 'BlockBlob';
      }
      const uploadResponse = await fetch(uploadUrl, {
        method: 'PUT',
        body: file,
        headers,
      });

      if (!uploadResponse.ok) {
        const body = await uploadResponse.text().catch(() => '');
        console.error('S3 upload failed:', {
          status: uploadResponse.status,
          statusText: uploadResponse.statusText,
          body,
          url: uploadUrl.split('?')[0],
        });
        throw new Error(
          `Failed to upload file: ${uploadResponse.status} ${uploadResponse.statusText}`,
        );
      }

      // Step 3: Confirm upload with backend
      const confirmResult = await rpcClient.core.documents.confirmUpload({
        documentId,
        entityType,
        entityId,
        associationType,
        fileSizeBytes: file.size,
      });

      return {
        documentId: confirmResult.document.id,
        name: confirmResult.document.name,
        mimeType: confirmResult.document.mimeType,
        fileSizeBytes: confirmResult.document.fileSizeBytes,
        fileId: confirmResult.document.fileId,
      };
    },
    onSuccess: (data) => {
      showToast({
        title: 'Document Uploaded',
        message: `"${data.name}" has been uploaded successfully`,
        color: 'success',
        icon: 'check',
      });
    },
    onError: (error) => {
      showToast({
        title: 'Upload Failed',
        message: error.message || 'Failed to upload document',
        color: 'error',
        icon: 'x',
      });
    },
  });
}

interface RemoveEntityDocumentInput {
  documentId: string;
  entityType: string;
  entityId: string;
}

/**
 * Generic hook for removing a document association from any entity.
 * Removes the association and deletes the document if it has no other associations.
 *
 * Caller is responsible for cache invalidation / refetching on success.
 */
export function useRemoveEntityDocument() {
  const { showToast } = useToast();

  return useMutation<RemoveDocumentResponse, Error, RemoveEntityDocumentInput>({
    mutationFn: async (input) => {
      return await rpcClient.core.documents.remove(input);
    },
    onSuccess: () => {
      showToast({
        title: 'Document Removed',
        message: 'Document has been removed successfully',
        color: 'success',
        icon: 'check',
      });
    },
    onError: (error) => {
      showToast({
        title: 'Removal Failed',
        message: error.message || 'Failed to remove document',
        color: 'error',
        icon: 'x',
      });
    },
  });
}
